import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as _ from "lodash";

import { setGlobalModal } from "src/core/shared/store/global/global.actions";
import type { IGlobalContextModal } from "src/core/shared/store/global/global.reducer";
import type { IStore } from "src/core/shared/store/index";
import useSetCurrentList from "src/core/lists/hooks/useSetCurrentList";
import { useSetCurrentOrderMode } from "src/core/orders/hooks/useSetCurrentOrder";
import type { ContextType } from "src/core/shared/hooks/menu/useMenu";
import type { IRoute } from "src/pages/Router/routes";
import { setDrop } from "src/core/drops/store/drops.actions";
import { setCustomer } from "src/core/customers/store/customers.actions";
import { ordersApi } from "src/core/orders/api/orders.api";

import useGetMenuItemByPath from "./useGetMenuItemByPath";

const useBeforeRemoveContext = () => {
  const {
    currentOrder, currentList, orderStatuses, masqueradeMode
  } = useMappedState(({
    orders, lists, global
  }: IStore) => ({
    currentOrder: orders.currentOrder,
    currentList: lists.currentList,
    orderStatuses: global.settings?.orderStatuses,
    masqueradeMode: global.masqueradeMode
  }));

  // no need to handle response, if this fails it's not critical
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ _deleteOrderRes, deleteOrderReq ] = ordersApi("DELETE_ORDER");
  const [ , updateOrderReq ] = ordersApi("UPDATE_ORDER");
  const setCurrentOrderMode = useSetCurrentOrderMode();
  const setCurrentList = useSetCurrentList();
  const getItemByPath = useGetMenuItemByPath();
  const dispatch = useDispatch();

  const setModalData = React.useCallback((modalData: IGlobalContextModal | null) => {
    dispatch(setGlobalModal(modalData));
  }, []);

  const currentOrderId = React.useMemo(() => currentOrder.data?.id, [ currentOrder.data?.id ]);
  const currentOrderCreated = React.useMemo(() => currentOrder.data?.createdAt, [ currentOrder.data?.createdAt ]);
  const currentOrderUpdated = React.useMemo(() => currentOrder.data?.updatedAt, [ currentOrder.data?.updatedAt ]);
  const currentOrderStatusId = React.useMemo(() => currentOrder.data?.orderStatusId, [ currentOrder.data?.orderStatusId ]);
  const currentOrderSaved = React.useMemo(() => currentOrder.saved, [ currentOrder.saved ]);
  const currentOrderHasData = React.useMemo(() => !!currentOrder.data, [ currentOrder.data ]);
  const currentOrderHasProducts = React.useMemo(() => !!currentOrder.data?.orderLines.length, [ currentOrder.data?.orderLines ]);

  const beforeRemoveContext = React.useCallback(
    (type: ContextType, routes: IRoute[], force = false) => {
      switch (type) {
        case "ORDER_PRODUCT_SELECTION":
          if (currentOrderHasData) {
            if (location.pathname.includes(`/orders/${currentOrderId}/order-placed`)) {
              return true;
            } else {
              if (force || currentOrderSaved) {
                setCurrentOrderMode("view");
                setModalData(null);
                const orderStatus = _.find(orderStatuses, os => os.id === currentOrderStatusId);
                const isFirstSave = currentOrderCreated === currentOrderUpdated;

                if (orderStatus?.settings.group === "DRAFT" && isFirstSave && currentOrderId) {
                  deleteOrderReq(currentOrderId);
                }

                return true;
              } else {
                const currentRoute = getItemByPath(location.pathname, routes);

                setModalData({
                  title: "You are editing an order",
                  details: "Are you sure you want to leave this order? Any unsaved changes will be lost!",
                  type: "ORDER_PRODUCT_SELECTION",
                  origin:
                    currentRoute && currentRoute.allowedContexts && currentRoute.allowedContexts.includes("ORDER_PRODUCT_SELECTION") ?
                      location.pathname :
                      "/products"
                });

                return false;
              }
            }
          }

          return true;

        case "LIST_PRODUCT_SELECTION":
          if (currentList.data) {
            if (force || currentList.saved) {
              setModalData(null);

              setCurrentList({
                ...currentList,
                mode: "view"
              });

              return true;
            } else {
              const currentRoute = getItemByPath(location.pathname, routes);

              setModalData({
                title: "You are editing a list",
                details: "Are you sure you want to leave this list? Any unsaved changes will be lost!",
                type: "LIST_PRODUCT_SELECTION",
                origin:
                  currentRoute && currentRoute.allowedContexts && currentRoute.allowedContexts.includes("LIST_PRODUCT_SELECTION") ?
                    location.pathname :
                    "/products"
              });

              return false;
            }
          }

          return true;

        case "CREATE_EDIT_DROP":
          dispatch(setDrop(null));

          return true;

        case "CREATE_EDIT_CUSTOMER":
          // When entering masquerade mode customer and drop is set before this runs
          // only overwrite when masquerade mode is false
          if (!masqueradeMode?.active) {
            dispatch(setDrop(null));
            dispatch(setCustomer(null));
          }

          return true;

        default:
          return true;
      }
    },
    [
      currentOrderHasData,
      currentList,
      dispatch,
      currentOrderId,
      currentOrderSaved,
      setCurrentOrderMode,
      setModalData,
      orderStatuses,
      currentOrderCreated,
      currentOrderUpdated,
      currentOrderStatusId,
      getItemByPath,
      setCurrentList,
      masqueradeMode
    ]
  );

  return beforeRemoveContext;
};

export default useBeforeRemoveContext;
